/**
 * 68.42: the commander's table at planet scale, in play (the owner, Oct 19: "I like planet table … support 2 projections,
 * globe and flat projected map … Sort of thinking A and B"). At the commander's ladder's two far stops — the overview
 * (the theatre) and the orbit (the planet) — the table draws the side's view of the match (ui/planetTable.ts) in one of
 * two projections, one drawing for both (ui/planetTableDraw.ts):
 * - **globe**: the game's own planet, the table's ground layer draped on it (rendering/planetTableDrape.ts), the upright
 *   marks on a canvas over it, hidden where the ground stands between the eye and them;
 * - **map**: a flat map over the view (ui/planetTableMap.ts), azimuthal equidistant about the theatre's centre (map
 *   distance is walking distance), centred where the table's view is centred — a toggle keeps the centre and the
 *   selection. A click on the map names the place under it (`hereAt`).
 * The command overlay stands down to its card, drag box, hover ring and your unit's ring there (`CommandOverlay.draw`'s
 * `bare`). The Lab's Planet table row turns it on (off by default: nothing is built, nothing drawn); `.` or the toggle on
 * the screen flips the projection. The main routes (core/planetRoutes.ts `mainRoutes`, lane F's: base to base and each base
 * to each objective, on wheels) are read once a match, off the frame that opens the table (the first call builds the route
 * graph's fields: ~100 ms on the megaplanet, unless the setup's dressing already did), then kept; the roads (GameConfig.roads,
 * core/roads.ts `roadLines`) with them, drawn from the theatre in. Presentation only: reads
 * the state, writes none.
 *
 * Costs (stated in 68.42): the view rebuilt at most 4 × a second; the upright marks redrawn when it is or the camera
 * moved; the drape's texture (2048²) redrawn at most twice a second, only on a change; the map's ground sampled once per
 * window (1.3 × the view's, coarse then full, a few milliseconds a frame; re-sampled when the centre nears its edge or the zoom changes).
 */
import * as THREE from 'three';
import type { GameState, Vec3 } from '../types';
import type { CommandTable } from '../core/commandTable';
import { planetTableOf, tableWindow, type PlanetTable } from '../ui/planetTable';
import { drawTable, drawScaleBar, type M, type TableProj, type TableZoom } from '../ui/planetTableDraw';
import { MapGroundJob, mapProjection, type MapWindow } from '../ui/planetTableMap';
import { PlanetTableDrape, globeProjection } from '../rendering/planetTableDrape';
import { fromMap, toMap } from '../ui/topoMap';
import { mainRoutes } from '../core/planetRoutes';
import { roadsOf, roadLines } from '../core/roads';
import { worldSheetOf } from '../core/worldSheet';
import { climateOf, type ClimateCellId } from '../core/climate';
import { woodsDensityOf } from '../core/woodsDensity';
import { frontsOf } from '../ui/frontMarks';
import { PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { PHYS_DT } from '../constants';
import { personSetOf } from '../core/scaleProfile';

/** 74.3 step 0: the Lab's World sheet row (`?worldsheet=1` without the Lab): the watershed, the lakes and the chokepoint candidates drawn on the table. */
let sheetOverride: boolean | null = null;
export function setWorldSheetShown(on: boolean | null): void { sheetOverride = on; }
export function worldSheetShown(): boolean {
  if (sheetOverride !== null) return sheetOverride;
  try { return typeof location !== 'undefined' && new URLSearchParams(location.search).get('worldsheet') === '1'; } catch { return false; }
}
/** 74.12 step 1: the Lab's Climate sheet row (`?climate=1` without the Lab): the climate cells drawn on the table, with or without the world sheet's overlays. */
let climateOverride: boolean | null = null;
export function setClimateShown(on: boolean | null): void { climateOverride = on; }
export function climateShown(): boolean {
  if (climateOverride !== null) return climateOverride;
  try { return typeof location !== 'undefined' && new URLSearchParams(location.search).get('climate') === '1'; } catch { return false; }
}
/** 393 (3) step 1: the Lab's Woods density sheet row (`?woods=1` without the Lab): the woods density field drawn on the table as the climate is. */
let woodsOverride: boolean | null = null;
export function setWoodsShown(on: boolean | null): void { woodsOverride = on; }
export function woodsShown(): boolean {
  if (woodsOverride !== null) return woodsOverride;
  try { return typeof location !== 'undefined' && new URLSearchParams(location.search).get('woods') === '1'; } catch { return false; }
}
/** The table draws at most this many climate tiles (every k-th vertex): a megaplanet's sheet has tens of thousands, the drape redraws twice a second. */
export const CLIMATE_DISCS = 12000;

export type TableProjection = 'globe' | 'map';
export type PlanetTableSetting = 'off' | TableProjection;

let settingOverride: PlanetTableSetting | null = null;
/** The Lab's Planet table row (its launch); null goes back to the page's `?planettable=globe|map` / off. */
export function setPlanetTable(v: PlanetTableSetting | null): void { settingOverride = v; }
/** How a match starts: off, or on in this projection. */
export function planetTableSetting(): PlanetTableSetting {
  if (settingOverride !== null) return settingOverride;
  try { const v = new URLSearchParams(location.search).get('planettable'); return v === 'globe' || v === 'map' ? v : 'off'; } catch { return 'off'; }
}

const VIEW_HZ = 4, DRAPE_HZ = 2;
/** The map's ground sampling a frame (ms). */
const GROUND_MS = 3;
/** The map's ground covers this much more than the view (a pan within it needs no new ground). */
const GROUND_SPAN = 1.3;

export class PlanetTableView {
  projection: TableProjection;
  private table: PlanetTable | null = null;
  private tableAt = -Infinity;
  private drape: PlanetTableDrape | null = null;
  private drapeFor = '';
  private drapeAt = -Infinity;
  private readonly marks = document.createElement('canvas');
  private readonly map = document.createElement('canvas');
  private readonly toggleEl = document.createElement('div');
  /** the map's ground drawn, and the one being sampled (a few rows a frame) */
  private ground: MapGroundJob | null = null;
  private groundJob: MapGroundJob | null = null;
  private proj: TableProj | null = null;
  private drawnFor = '';
  private window: MapWindow | null = null;
  /** each squad's place on the screen as drawn (a group plate stands for its squads at the theatre's zoom) */
  readonly squadAt = new Map<number, { x: number; y: number }>();
  /** each squad's group (a click on a plate selects it all) */
  readonly groupOf = new Map<number, number[]>();
  /** the match's main routes, once read (`routesFor`: the match they're for — its planets and board); null until then */
  private routes: Array<{ planetIndex: number; line: Vec3[]; main: boolean }> | null = null;
  /** and its roads (GameConfig.roads), read with them */
  private roads: Array<{ planetIndex: number; line: Vec3[]; trail?: boolean }> = [];
  /** 74.3 step 0: the world sheet's overlays, read once with the routes when the Lab's row shows them */
  private sheet: { planetIndex: number; chains: Array<{ line: Vec3[]; order: number }>; lakes: Array<{ at: Vec3[]; r: number; playa?: boolean }>; candidates: Array<{ at: Vec3; kind: 'pass' | 'ford' | 'crossing' }>; climate?: { at: Vec3[]; cell: ClimateCellId[]; r: number }; woods?: { at: Vec3[]; density: number[]; r: number } } | null = null;
  private routesFor: unknown = null;
  private routesPending = false;
  /** the last frame's cost, ms (the F3 overlay may show it) */
  lastMs = 0;

  constructor(private readonly scene: THREE.Scene, projection: TableProjection, private readonly onToggle: () => void = () => {}) {
    this.projection = projection;
    this.marks.style.cssText = 'position:fixed; inset:0; width:100vw; height:100vh; pointer-events:none; z-index:19; display:none;';
    this.map.style.cssText = 'position:fixed; inset:0; width:100vw; height:100vh; pointer-events:none; z-index:18; display:none;';
    this.toggleEl.style.cssText = 'position:fixed; right:16px; top:64px; z-index:22; display:none; font:12px system-ui, sans-serif; background:rgba(12,12,24,0.88); border:1px solid rgba(255,255,255,0.15); border-radius:8px; padding:3px; gap:3px; user-select:none;';
    this.toggleEl.addEventListener('mousedown', e => { e.stopPropagation(); const t = (e.target as HTMLElement).closest('[data-p]') as HTMLElement | null; if (t && t.dataset.p !== this.projection) this.toggle(); });
    document.body.append(this.map, this.marks, this.toggleEl);
  }

  /** Whether the table drew this frame (the host then draws the command overlay bare, and reads places off the map). */
  get active(): boolean { return this.marks.style.display !== 'none'; }

  /** Flip the projection (`.`, or the toggle on the screen). */
  toggle(): void {
    this.projection = this.projection === 'globe' ? 'map' : 'globe';
    this.drawnFor = '';
    this.onToggle();
  }

  /** Draw the table for this frame: `zoom` from the ladder's stop, `centre` the table's view (a direction), `alpha` the view's blend. */
  update(a: { state: GameState; team: number; tick: number; picks: CommandTable; camera: THREE.PerspectiveCamera; zoom: Exclude<TableZoom, 'sector'>; centre: Vec3 | null; alpha: number; selected: ReadonlySet<number>; now: number }): void {
    const t0 = performance.now();
    if (!this.table || a.now - this.tableAt >= 1 / VIEW_HZ) {
      const v0 = performance.now();
      this.readRoutes(a.state);
      const person = PERSON_BODY_HEIGHT * personSetOf(a.state).k, planetsHeld = [...new Set(a.state.players.map(p => p.planetIndex))];
      const fronts = planetsHeld.flatMap(pi => frontsOf(a.state, pi, a.tick * PHYS_DT, person).map(f => ({ ...f, planetIndex: pi })));   // 74.11 (b): the fronts at planet scale (the cells on)
      this.table = planetTableOf(a.state, a.team, a.tick, a.picks, { routes: this.routes ?? [], roads: this.roads, ...(this.sheet ? { sheet: this.sheet } : {}), ...(fronts.length ? { fronts } : {}) });
      this.marks.dataset.viewMs = (performance.now() - v0).toFixed(2);
      this.tableAt = a.now;
      this.drawnFor = '';
    }
    const t = this.table;
    if (!t) { this.hide(); return; }
    const planet = a.state.planets[t.frame.planetIndex];
    const origin = new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z);
    const w = window.innerWidth, h = window.innerHeight, dpr = Math.min(2, window.devicePixelRatio || 1);
    const centre: M = a.centre ? toMap(t.frame, a.centre) : { x: 0, y: 0 };
    const win = tableWindow(t, a.zoom);
    this.window = { x: centre.x, y: centre.y, extent: win.extent };
    // the selected group (the one a selected squad stands in)
    this.groupOf.clear();
    let selected: string | null = null;
    for (const [key, ids] of t.groupSquads) { for (const id of ids) this.groupOf.set(id, ids); if (ids.some(id => a.selected.has(id))) selected ??= key; }
    const opts = { zoom: a.zoom as TableZoom, window: { x: this.window.x, y: this.window.y, extent: Math.min(win.extent * 1.7, Math.PI * t.frame.radius * 0.9) }, selected };
    // what this frame shows: redraw only when it changed (the view, the camera, the projection, the size)
    const cam = a.camera.matrixWorld.elements.map(x => x.toFixed(4)).join(',');
    const key = `${this.projection}|${a.zoom}|${t.key}|${[...a.selected].join(',')}|${w}x${h}|${this.projection === 'globe' ? cam : `${Math.round(centre.x)},${Math.round(centre.y)}`}|${this.tableAt}`;
    this.marks.style.display = ''; this.marks.style.opacity = String(a.alpha);
    this.toggleEl.style.display = 'flex';
    this.toggleEl.innerHTML = (['globe', 'map'] as const).map(p => `<span data-p="${p}" style="cursor:pointer;padding:3px 10px;border-radius:6px;${p === this.projection ? 'background:#24304c;color:#eef1f6;' : 'color:#9aa2b2;'}">${p}</span>`).join('') + '<span style="color:#6c7486;padding:3px 6px;">.</span>';
    if (key === this.drawnFor) { this.lastMs = performance.now() - t0; return; }
    this.drawnFor = key;
    if (this.marks.width !== Math.round(w * dpr) || this.marks.height !== Math.round(h * dpr)) { this.marks.width = Math.round(w * dpr); this.marks.height = Math.round(h * dpr); }
    const g = this.marks.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0); g.clearRect(0, 0, w, h);
    if (this.projection === 'globe') {
      this.map.style.display = 'none';
      this.proj = globeProjection(a.camera, planet, t.frame, t.person, origin, w, h);
      this.drapeOn(t, planet, a, opts, origin);
    } else {
      if (this.drape) this.drape.mesh.visible = false;
      this.map.style.display = ''; this.map.style.opacity = String(a.alpha);
      this.proj = mapProjection(this.window, w, h);
      if (this.drawMap(t, planet, w, h, dpr)) this.drawnFor = '';   // (a sample under way: draw again next frame)
      drawTable(g, this.proj, t.scene, opts, 'ground');
    }
    drawTable(g, this.proj, t.scene, opts, 'up');
    drawScaleBar(g, w, h, this.proj.ppp(centre), this.projection === 'globe' ? ' (at the centre)' : '');
    this.marks.dataset.ms = (performance.now() - t0).toFixed(2);   // (what a redraw costs, for the measurements in 68.42)
    // where each squad is drawn: its block in the sector, its group's plate at the theatre (the plate's place, as drawn)
    this.squadAt.clear();
    for (const grp of t.scene.groups) {
      const pts = grp.squads.map(q => this.proj!.at(q.at)).filter(p => p.ok);
      if (!pts.length) continue;
      const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length, cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
      for (const id of t.groupSquads.get(grp.key) ?? []) this.squadAt.set(id, { x: cx, y: cy });
    }
    this.lastMs = performance.now() - t0;
  }

  /** The globe's drape: on the planet, its texture redrawn at most DRAPE_HZ, and only when what it shows changed. */
  private drapeOn(t: PlanetTable, planet: GameState['planets'][number], a: { zoom: Exclude<TableZoom, 'sector'>; now: number }, opts: Parameters<typeof drawTable>[3], origin: THREE.Vector3): void {
    if (!this.drape) {
      this.drape = new PlanetTableDrape(planet, t.frame, t.person, origin);
      this.scene.add(this.drape.mesh);
    }
    this.drape.mesh.visible = true;
    const E = a.zoom === 'planet' ? Math.PI * t.frame.radius * 0.8 : Math.min(this.window!.extent * 1.6, Math.PI * t.frame.radius * 0.92);
    this.drape.setExtent(E);
    const sppp = 0.7 * this.proj!.ppp(this.window!);
    const scaleMoved = this.drape.sppp > 0 && Math.abs(Math.log(sppp / this.drape.sppp)) > Math.log(1.4);
    const want = `${a.zoom}|${t.key}|${opts.selected}`;
    if ((want !== this.drapeFor || scaleMoved) && a.now - this.drapeAt >= 1 / DRAPE_HZ) {
      const d0 = performance.now();
      this.drape.draw(t.scene, opts, sppp);
      this.marks.dataset.drapeMs = (performance.now() - d0).toFixed(2);
      this.drapeFor = want; this.drapeAt = a.now;
    }
  }

  /** The flat map's ground: sampled for a window GROUND_SPAN × the view's (a few rows a frame), re-sampled when the view nears
   *  its edge or the zoom changes; a coarse pass first, the last ground drawn under the view meanwhile. True while a sample is under way. */
  private drawMap(t: PlanetTable, planet: GameState['planets'][number], w: number, h: number, dpr: number): boolean {
    const v = this.window!;
    // a quick coarse ground first (every 15 px), then the full one (every 5 px), the drawn one under the view meanwhile
    const fits = (c: MapGroundJob | null) => !!c && c.w === Math.round(w * GROUND_SPAN) && Math.abs(c.v.extent / GROUND_SPAN - v.extent) < 1e-6 && Math.abs(c.v.x - v.x) < v.extent * 0.15 && Math.abs(c.v.y - v.y) < v.extent * 0.15;
    const job = (cell: number) => new MapGroundJob(planet, t.frame, t.person, { x: v.x, y: v.y, extent: v.extent * GROUND_SPAN }, Math.round(w * GROUND_SPAN), Math.round(h * GROUND_SPAN), t.scene.control, t.scene.own, t.scene.them, cell, t.scene.contested);
    if (!fits(this.ground) && !fits(this.groundJob)) this.groundJob = job(15);
    if (this.groundJob) {
      const g0 = performance.now();
      if (this.groundJob.step(GROUND_MS)) { this.ground = this.groundJob; this.groundJob = this.ground.cell > 5 ? job(5) : null; }
      this.marks.dataset.groundMs = (performance.now() - g0).toFixed(2);
    }
    if (this.map.width !== Math.round(w * dpr) || this.map.height !== Math.round(h * dpr)) { this.map.width = Math.round(w * dpr); this.map.height = Math.round(h * dpr); }
    const g = this.map.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.fillStyle = '#0b0d14'; g.fillRect(0, 0, w, h);
    const gnd = this.ground;
    if (gnd) {   // the ground drawn, placed where the view's window falls in it
      const s = Math.min(w, h) / (2 * v.extent), gs = Math.min(gnd.w, gnd.h) / (2 * gnd.v.extent);
      const k = s / gs, gx = gnd.w / 2 + (v.x - gnd.v.x) * gs, gy = gnd.h / 2 - (v.y - gnd.v.y) * gs;
      g.drawImage(gnd.canvas, w / 2 - gx * k, h / 2 - gy * k, gnd.w * k, gnd.h * k);
    }
    return !!this.groundJob;
  }

  /** On the map: the place under a screen point (a direction on the table's planet); null on the globe or off. */
  hereAt(x: number, y: number): { planetIndex: number; dir: Vec3 } | null {
    if (!this.active || this.projection !== 'map' || !this.table || !this.window) return null;
    const v = this.window, s = Math.min(window.innerWidth, window.innerHeight) / (2 * v.extent);
    const mx = v.x + (x - window.innerWidth / 2) / s, my = v.y - (y - window.innerHeight / 2) / s;
    if (Math.hypot(mx, my) > Math.PI * this.table.frame.radius * 0.98) return null;
    return { planetIndex: this.table.frame.planetIndex, dir: fromMap(this.table.frame, mx, my) };
  }

  hide(): void {
    this.marks.style.display = 'none'; this.map.style.display = 'none'; this.toggleEl.style.display = 'none';
    if (this.drape) this.drape.mesh.visible = false;
    this.squadAt.clear(); this.groupOf.clear();
    this.drawnFor = '';
  }

  /** Read the match's main routes once, after this frame (a new match's planets or board read them again). */
  private readRoutes(state: GameState): void {
    const forMatch = state.board ?? state.planets;
    if (this.routesFor === forMatch || this.routesPending) return;
    this.routesPending = true;
    setTimeout(() => {
      const r0 = performance.now();
      const roads = new Map<number, ReturnType<typeof roadsOf>>(), roadsFor = (pi: number) => { if (!roads.has(pi)) roads.set(pi, roadsOf(state, pi)); return roads.get(pi)!; };
      this.routes = mainRoutes(state, roadsFor).map(r => ({ planetIndex: r.planetIndex, line: r.points, main: true }));
      this.sheet = null;
      const showSheet = worldSheetShown(), showClimate = climateShown(), showWoods = woodsShown();
      if (showSheet || showClimate || showWoods) {   // 74.3 step 0: the sheet of the planet the table shows (the battle world); 74.12 step 1: its climate; 393 (3) step 1: its woods
        const pi = this.routes[0]?.planetIndex ?? state.board?.bases[0]?.planetIndex ?? 0, planet = state.planets[pi];
        if (planet) {
          const sheet = worldSheetOf(planet), g = sheet.graph;
          const point = (v: number): Vec3 => ({ x: g.dirs[3 * v], y: g.dirs[3 * v + 1], z: g.dirs[3 * v + 2] });   // (the table maps surface directions, as the roads' lines are)
          // a lake's vertices under its level, each drawn the mesh's edge across (in people: half the first edge's chord)
          const person = personSetOf(state).k, e0 = g.start[0] < g.start[1] ? g.nbr[g.start[0]] : 0;
          const edgePeople = Math.hypot(g.dirs[0] - g.dirs[3 * e0], g.dirs[1] - g.dirs[3 * e0 + 1], g.dirs[2] - g.dirs[3 * e0 + 2]) * planet.radius / person;
          this.sheet = {
            planetIndex: pi,
            chains: showSheet ? sheet.chains.map(c => ({ line: c.vertices.map(point), order: c.order })) : [],
            lakes: showSheet ? sheet.basins.filter(b => b.lake || b.playa).map(b => ({ at: b.vertices.filter(v => sheet.radius[v] <= b.spillRadius).map(point), r: 0.8 * edgePeople, ...(b.playa ? { playa: true } : {}) })) : [],
            candidates: showSheet ? sheet.candidates.map(c => ({ at: point(c.vertex), kind: c.kind })) : [],
          };
          if (showClimate) {   // 74.12 step 1: a tile per sampled vertex in its cell's colour, the sampled spacing across (every k-th vertex, so a megaplanet stays under CLIMATE_DISCS)
            const c = climateOf(planet, sheet), step = Math.max(1, Math.ceil(g.count / CLIMATE_DISCS)), at: Vec3[] = [], cell: ClimateCellId[] = [];
            for (let v = 0; v < g.count; v += step) { at.push(point(v)); cell.push(c.cell[v]); }
            this.sheet.climate = { at, cell, r: 0.5 * edgePeople * Math.sqrt(step) };   // (half the sampled spacing: the tiles meet, they do not stack)
          }
          if (showWoods) {   // 393 (3) step 1: the woods density field, a tile a sampled vertex (the forest's basis planet: the generated ground)
            const w = woodsDensityOf(planet, state.config.seed ?? '', PERSON_BODY_HEIGHT * person), step = Math.max(1, Math.ceil(g.count / CLIMATE_DISCS)), at: Vec3[] = [], density: number[] = [];
            for (let v = 0; v < g.count; v += step) { at.push(point(v)); density.push(w.stand[v]); }   // (393 (3) step 3: the stands' read — the riparian floor and the windward thinning in)
            this.sheet.woods = { at, density, r: 0.5 * edgePeople * Math.sqrt(step) };
          }
        }
      }
      this.roads = [...new Set(state.players.map(p => p.planetIndex))].sort((a, b) => a - b).flatMap(pi => { const r = roadsFor(pi); return r ? [...roadLines(state.planets[pi], r).map(line => ({ planetIndex: pi, line })), ...roadLines(state.planets[pi], r, 'trails').map(line => ({ planetIndex: pi, line, trail: true }))] : []; });
      this.marks.dataset.routesMs = (performance.now() - r0).toFixed(1);
      this.routesFor = forMatch; this.routesPending = false;
      this.tableAt = -Infinity;   // (the next frame rebuilds the view with them)
    }, 0);
  }

  dispose(): void {
    this.hide();
    if (this.drape) { this.scene.remove(this.drape.mesh); this.drape.dispose(); this.drape = null; }
    this.marks.remove(); this.map.remove(); this.toggleEl.remove();
  }
}
